import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Text,
  useRestoreFocusTarget,
} from "@fluentui/react-components";
import {
  ArrowExpand24Regular,
  ChevronLeft24Regular,
  ChevronRight24Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import { useCallback, useEffect, useState } from "react";
import { usePagedImagePreviewStyles } from "./PagedImagePreview.styles";
import {
  IPagedImagePreviewProps,
  ISlideControlsProps,
  ISlideImageProps,
} from "./PagedImagePreview.types";

const SlideImage = ({
  imageUrl,
  currentPageIndex,
  totalPages,
  isImageLoading,
  onImageLoad,
  onImageError,
  imageWrapperClassName,
  imageClassName,
}: ISlideImageProps): JSX.Element => {
  const styles = usePagedImagePreviewStyles();

  return (
    <div className={imageWrapperClassName}>
      {isImageLoading && (
        <div className={styles.imageLoadingOverlay}>
          <Spinner size="small" />
        </div>
      )}
      <img
        src={imageUrl}
        alt={`Slide ${currentPageIndex + 1} of ${totalPages}`}
        className={imageClassName}
        onLoad={onImageLoad}
        onError={onImageError}
        draggable={false}
      />
    </div>
  );
};

const SlideControls = ({
  currentPageIndex,
  totalPages,
  canGoToPrevious,
  canGoToNext,
  onPreviousPage,
  onNextPage,
  trailingAction,
}: ISlideControlsProps): JSX.Element => {
  const styles = usePagedImagePreviewStyles();

  return (
    <div className={styles.controls}>
      <Button
        appearance="outline"
        icon={<ChevronLeft24Regular />}
        onClick={onPreviousPage}
        disabled={!canGoToPrevious}
        aria-label={`Go to previous slide (slide ${Math.max(1, currentPageIndex)})`}
      />
      <Text className={styles.pageCounter}>
        Slide {currentPageIndex + 1} of {totalPages}
      </Text>
      <Button
        appearance="outline"
        icon={<ChevronRight24Regular />}
        onClick={onNextPage}
        disabled={!canGoToNext}
        aria-label={`Go to next slide (slide ${Math.min(totalPages, currentPageIndex + 2)})`}
      />
      {trailingAction}
    </div>
  );
};

export const PagedImagePreview = ({
  slideImageUrls,
  conversionStatus,
}: IPagedImagePreviewProps): JSX.Element => {
  const styles = usePagedImagePreviewStyles();
  const restoreFocusTargetAttribute = useRestoreFocusTarget();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const isReadyWithSlides =
    conversionStatus === "ready" && slideImageUrls.length > 0;

  const currentImageUrl = slideImageUrls[currentPageIndex] ?? null;
  const totalPages = slideImageUrls.length;
  const canGoToPrevious = currentPageIndex > 0;
  const canGoToNext = currentPageIndex < totalPages - 1;

  const handlePreviousPage = useCallback(() => {
    setCurrentPageIndex((prev) => Math.max(0, prev - 1));
    setIsImageLoading(true);
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPageIndex((prev) =>
      Math.min(slideImageUrls.length - 1, prev + 1),
    );
    setIsImageLoading(true);
  }, [slideImageUrls.length]);

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  useEffect(() => {
    if (isExpanded && !isReadyWithSlides) {
      setIsExpanded(false);
    }
  }, [isExpanded, isReadyWithSlides]);

  useEffect(() => {
    if (!isExpanded || !isReadyWithSlides) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }

      if (event.key === "ArrowLeft" && canGoToPrevious) {
        event.preventDefault();
        handlePreviousPage();
      } else if (event.key === "ArrowRight" && canGoToNext) {
        event.preventDefault();
        handleNextPage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isExpanded,
    isReadyWithSlides,
    canGoToPrevious,
    canGoToNext,
    handlePreviousPage,
    handleNextPage,
  ]);

  if (conversionStatus === "pending") {
    return (
      <div
        className={styles.processingContainer}
        role="status"
        aria-live="polite"
      >
        <Spinner label="Processing document... This may take a moment." />
      </div>
    );
  }

  if (conversionStatus === "failed") {
    return (
      <div className={styles.errorContainer} role="alert">
        <Text className={styles.errorHeading}>Unable to process document</Text>
        <Text className={styles.errorBody}>
          The document could not be converted. Please try uploading a different
          file.
        </Text>
      </div>
    );
  }

  if (conversionStatus === "not_applicable") {
    return (
      <div className={styles.errorContainer} role="alert">
        <Text className={styles.errorHeading}>Preview not available</Text>
        <Text className={styles.errorBody}>
          This document type does not support preview.
        </Text>
      </div>
    );
  }

  if (totalPages === 0 || !currentImageUrl) {
    return (
      <div className={styles.errorContainer} role="alert">
        <Text className={styles.errorHeading}>No pages available</Text>
        <Text className={styles.errorBody}>
          The document appears to be empty.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.carouselContainer}>
        <SlideImage
          imageUrl={currentImageUrl}
          currentPageIndex={currentPageIndex}
          totalPages={totalPages}
          isImageLoading={isImageLoading}
          onImageLoad={() => setIsImageLoading(false)}
          onImageError={() => setIsImageLoading(false)}
          imageWrapperClassName={styles.imageWrapper}
          imageClassName={styles.image}
        />

        <SlideControls
          currentPageIndex={currentPageIndex}
          totalPages={totalPages}
          canGoToPrevious={canGoToPrevious}
          canGoToNext={canGoToNext}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          trailingAction={
            <Button
              {...restoreFocusTargetAttribute}
              appearance="outline"
              icon={<ArrowExpand24Regular />}
              onClick={handleExpand}
              aria-label="Expand to fullscreen"
            />
          }
        />
      </div>

      <Dialog
        open={isExpanded}
        onOpenChange={(_event, data) => setIsExpanded(data.open)}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody className={styles.fullscreenContent}>
            <div className={styles.fullscreenHeader}>
              <DialogTitle className={styles.fullscreenTitle}>
                Slide preview
              </DialogTitle>
              <Button
                appearance="outline"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
                aria-label="Close fullscreen"
              />
            </div>
            <DialogContent className={styles.fullscreenContent}>
              <SlideImage
                imageUrl={currentImageUrl}
                currentPageIndex={currentPageIndex}
                totalPages={totalPages}
                isImageLoading={isImageLoading}
                onImageLoad={() => setIsImageLoading(false)}
                onImageError={() => setIsImageLoading(false)}
                imageWrapperClassName={styles.fullscreenImageWrapper}
                imageClassName={styles.fullscreenImage}
              />
              <SlideControls
                currentPageIndex={currentPageIndex}
                totalPages={totalPages}
                canGoToPrevious={canGoToPrevious}
                canGoToNext={canGoToNext}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
